import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import React, { ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'underscore';
import Api from '../../../../api/Api';
import { IFWEPoint, IInsituPoint } from '../../../../api/endpoints/FileEndPoint';
import { QUERY_KEYS } from '../../../../queries/queryKeys';
import useMapStore from '../../../../store/mapStore';
import { useMarkerStore } from '../../../../store/markerStore';
import { usePointStore } from '../../../../store/pointStore';
import { MarkerItem, surfaceMaps } from '../../../../types/home';
import { DatasetType } from '../../../../types/marker';
import { SEARCH_PARAM } from '../../../../utils/common';
import { downloadCheckboxList } from '../../../../utils/downloadCheckbox';
import { getNextFreeNumber } from '../../../../utils/getNexFreeNumber';
import guid from '../../../../utils/Guid';
import { toFixed } from '../../../../utils/toFixed';
import { IWmsProps } from '../Layer/DatasetMesh';
import {
  FWELAYER,
  INSITU_EXPANDED_LAYER,
  INSITU_LAYER,
  INSITU_UPLOAD_LAYER,
} from '../Layer/MarkerLayer';
import { createExpandedPoints } from '../MapHelper';
import FwePointPopup from '../PointPopup/FWE/FwePointPopup';
import InsituPointPopup from '../PointPopup/Insitu/InsituPointPopup';

interface MapPopupsReturn {
  fwePoint: IFWEPoint | null;
  insituPoint: IInsituPoint | null;
  expandedPoint: IInsituPoint | null;
  confirmationDeletePopup: boolean;
  setConfirmationDeletePopup: React.Dispatch<React.SetStateAction<boolean>>;
  notAuthorizedUserPopup: boolean;
  setNotAuthorizedUserPopup: React.Dispatch<React.SetStateAction<boolean>>;
  confirmationDeletePopupInsitu: boolean;
  setConfirmationDeletePopupInsitu: React.Dispatch<React.SetStateAction<boolean>>;
  loadingDelete: boolean;
  expandedPoints: IInsituPoint[] | null;
  setExpandedPoints: React.Dispatch<React.SetStateAction<IInsituPoint[] | null>>;
  handlePopupCoordinatesChanged: (
    pointParam: IFWEPoint | IInsituPoint,
    layerId: string,
    skipDuplicateCheck?: boolean
  ) => void;
  handleClosePopup: () => void;
  handleDeleteDatasetInsitu: () => Promise<void>;
  handleDeleteDataset: () => Promise<void>;
  handleRequestDeleteDataset: () => Promise<void>;
  popupContent: () => ReactNode;
  activePopupCoordinates: () => number[] | null;
  triggerMapClick: (
    coordinates: number[],
    fwePointName?: string,
    insituPointName?: string
  ) => Promise<void>;
}

export function useMapPopups(
  shoppingItems: any,
  setMeshUrl: (item?: IWmsProps) => void
): MapPopupsReturn {
  const [fwePoint, setFwePoint] = useState<IFWEPoint | null>(null);
  const [insituPoint, setInsituPoint] = useState<IInsituPoint | null>(null);
  const [expandedPoint, setExpandedPoint] = useState<IInsituPoint | null>(null);
  const [confirmationDeletePopup, setConfirmationDeletePopup] = useState<boolean>(false);
  const [notAuthorizedUserPopup, setNotAuthorizedUserPopup] = useState<boolean>(false);
  const [confirmationDeletePopupInsitu, setConfirmationDeletePopupInsitu] =
    useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

  const inSituPoints = usePointStore((state) => state.inSituPoints);
  const activePopupPoint = usePointStore((state) => state.activePopupPoint);
  const setInSituPoints = usePointStore((state) => state.setInSituPoints);
  const setFWEPoints = usePointStore((state) => state.setFWEPoints);
  const addPointName = usePointStore((state) => state.addPointName);
  const setAddPointName = usePointStore((state) => state.setAddPointName);

  const setAddMarkerMode = useMarkerStore((state) => state.setAddMarkerMode);
  const markers = useMarkerStore((state) => state.markers);
  const addMarker = useMarkerStore((state) => state.addMarker);

  const setUpdatedBounds = useMapStore((state) => state.setUpdatedBounds);

  const [expandedPoints, setExpandedPoints] = useState<any | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  function getLabelName(fwePointName?: string): string {
    if (fwePointName) return fwePointName;

    return 'P' + getNextFreeNumber(markers, shoppingItems);
  }

  const triggerMapClick = async (
    coordinates: number[],
    fwePointName?: string,
    insituPointName?: string
  ) => {
    setAddMarkerMode(false);
    setMeshUrl(undefined);
    setUpdatedBounds(true);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set(SEARCH_PARAM.COOR, JSON.stringify(coordinates));
    if (fwePointName) {
      const fwePointNameEncoded = encodeURIComponent(fwePointName).trim();
      searchParams.set(SEARCH_PARAM.FWEPOINTNAME, fwePointNameEncoded);
    }
    navigate(`/?${searchParams.toString()}`);
    const newMarker: MarkerItem = {
      id: guid(),
      label: addPointName && !isEmpty(addPointName) ? addPointName : getLabelName(fwePointName),
      coordinates: [toFixed(coordinates[0], 6), toFixed(coordinates[1], 6)],
      active: true,
      depth: 0,
      dataset: '',
      allDataset: {} as any,
      downloadPage: downloadCheckboxList(),
      key: DatasetType.SW,
      shapeFiles: [],
      surfaceMaps: {
        overlays: [],
        selectedLevel1: '',
        selectedLevel2: '',
        categories: [],
      } as surfaceMaps,
      fwePointName: fwePointName,
      insituPointName: insituPointName,
    };
    setAddPointName('');
    addMarker(newMarker);
  };

  const handlePopupCoordinatesChanged = (
    pointParam: IFWEPoint | IInsituPoint,
    layerId: string,
    skipDuplicateCheck = false
  ): void => {
    setFwePoint(null);
    setInsituPoint(null);

    if (layerId === INSITU_EXPANDED_LAYER) {
      setExpandedPoint(pointParam as IInsituPoint);
    } else if (layerId === FWELAYER) {
      setFwePoint(pointParam as IFWEPoint);
    } else if (layerId === INSITU_LAYER || layerId === INSITU_UPLOAD_LAYER) {
      if (skipDuplicateCheck) {
        setInsituPoint(pointParam as IInsituPoint);
        return;
      }

      const samePositionPoints = inSituPoints.filter(
        (p) => p.position[0] === pointParam.position[0] && p.position[1] === pointParam.position[1]
      );

      if (samePositionPoints.length === 1) {
        setInsituPoint(pointParam as IInsituPoint);
      } else {
        const expanded = createExpandedPoints(samePositionPoints);
        setExpandedPoints(expanded);
      }
    }
  };

  const handleClosePopup = (): void => {
    setFwePoint(null);
    setInsituPoint(null);
    setExpandedPoint(null);
  };

  const handleDeleteDatasetInsitu = useCallback(async (): Promise<void> => {
    try {
      setLoadingDelete(true);
      const point = insituPoint ?? expandedPoint;

      if (!point) {
        throw new Error('InSitu point does not exist');
      }

      const res = await Api.insituPoint.deleteInsituPoint(point.name || '');

      if (res?.Error) {
        const errMessage = res.Error?.ErrorMessage ?? `Failed to delete ${point.name}`;
        throw new Error(errMessage);
      }

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.map.insituPoints],
      });
      // The backend list can lag behind the deletion, and with the query's
      // 10-minute staleTime a stale refetch would keep the deleted station
      // alive in the store — drop it locally after the refetch settles.
      setInSituPoints(inSituPoints.filter((item) => item.name !== point.name));
      setConfirmationDeletePopupInsitu(false);
      handleClosePopup();
      setExpandedPoints([]);
      message.success('Delete completed');
    } catch (error: any) {
      message.error(error.message || 'Error deleting point');
    } finally {
      setLoadingDelete(false);
    }
  }, [insituPoint, expandedPoint, handleClosePopup, queryClient, inSituPoints, setInSituPoints]);

  const handleDeleteDataset = useCallback(async (): Promise<void> => {
    try {
      setLoadingDelete(true);
      await Api.service.deleteFWEdataset(fwePoint?.datasetId ?? '');
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.map.fwePoints],
      });
      setConfirmationDeletePopup(false);
      handleClosePopup();
      message.success('Dataset removed from MOOD');
    } catch (error) {
      console.error('Error deleting dataset:', error);
      message.error('Error deleting dataset');
    } finally {
      setLoadingDelete(false);
    }
  }, [fwePoint, handleClosePopup, queryClient, setFWEPoints]);

  const handleRequestDeleteDataset = useCallback(async (): Promise<void> => {
    try {
      setLoadingDelete(true);
      await Api.service.requestDeleteFWEdataset(fwePoint?.datasetId ?? '');
      message.success('Request Sent');
      setNotAuthorizedUserPopup(false);
      handleClosePopup();
    } catch (error) {
      console.error('Error sending request deleting dataset:', error);
      message.error('Error sending delete request');
    } finally {
      setLoadingDelete(false);
    }
  }, [fwePoint, handleClosePopup]);

  const popupContent = useCallback((): ReactNode => {
    if (activePopupPoint === FWELAYER && fwePoint) {
      return (
        <FwePointPopup
          setNotAuthorizedUserPopup={setNotAuthorizedUserPopup}
          setConfirmationDeletePopup={setConfirmationDeletePopup}
          loadingDelete={loadingDelete}
          setLoadingDelete={setLoadingDelete}
          coordinates={fwePoint.position}
          name={fwePoint.name}
          createNewPoint={triggerMapClick}
          fwePointOrigin={{
            tenant: fwePoint.tenant || '',
            folder: fwePoint.folder || '',
          }}
          datasetId={fwePoint.datasetId || ''}
          fweUrl={Api.FWE_URL}
          handleClose={() => setFwePoint(null)}
        />
      );
    } else if (activePopupPoint === INSITU_LAYER && insituPoint) {
      return (
        <InsituPointPopup
          coordinates={insituPoint.position}
          createNewPoint={triggerMapClick}
          name={insituPoint.name}
          timePeriod={{
            startDate: insituPoint.startDate || '',
            endDate: insituPoint.endDate || '',
          }}
          source={insituPoint.source || ''}
          handleClose={() => setInsituPoint(null)}
          setConfirmationDeletePopup={setConfirmationDeletePopupInsitu}
          loadingDelete={loadingDelete}
          setLoadingDelete={setLoadingDelete}
          type={insituPoint.type || ''}
        />
      );
    } else if (activePopupPoint === INSITU_EXPANDED_LAYER && expandedPoint) {
      return (
        <InsituPointPopup
          coordinates={expandedPoint.originalPosition ?? expandedPoint.position}
          createNewPoint={triggerMapClick}
          name={expandedPoint.name}
          timePeriod={{
            startDate: expandedPoint.startDate || '',
            endDate: expandedPoint.endDate || '',
          }}
          source={expandedPoint.source || ''}
          handleClose={() => setExpandedPoint(null)}
          setConfirmationDeletePopup={setConfirmationDeletePopupInsitu}
          loadingDelete={loadingDelete}
          setLoadingDelete={setLoadingDelete}
          type={expandedPoint.type || ''}
        />
      );
    }
    return null;
  }, [activePopupPoint, fwePoint, insituPoint, expandedPoint, loadingDelete, triggerMapClick]);

  const activePopupCoordinates = useCallback((): number[] | null => {
    if (activePopupPoint === FWELAYER && fwePoint) {
      return fwePoint.position;
    } else if (
      (activePopupPoint === INSITU_LAYER || activePopupPoint === INSITU_UPLOAD_LAYER) &&
      insituPoint
    ) {
      return insituPoint.position;
    } else if (activePopupPoint === INSITU_EXPANDED_LAYER && expandedPoint) {
      return expandedPoint.position;
    }
    return null;
  }, [fwePoint, insituPoint, expandedPoint, activePopupPoint]);

  return {
    fwePoint,
    insituPoint,
    expandedPoint,
    confirmationDeletePopup,
    setConfirmationDeletePopup,
    notAuthorizedUserPopup,
    setNotAuthorizedUserPopup,
    confirmationDeletePopupInsitu,
    setConfirmationDeletePopupInsitu,
    loadingDelete,
    expandedPoints,
    setExpandedPoints,
    handlePopupCoordinatesChanged,
    handleClosePopup,
    handleDeleteDatasetInsitu,
    handleDeleteDataset,
    handleRequestDeleteDataset,
    popupContent,
    activePopupCoordinates,
    triggerMapClick,
  };
}
